import React from 'react';
import {
  DataGrid,
  gridClasses,
  GridColDef,
  GridColumnHeaderParams,
  GridPaginationModel,
  GridRowModel,
  GridSortDirection,
  GridSortModel,
} from '@mui/x-data-grid';
import { Tooltip } from '@mui/material';
import Box from '@mui/system/Box';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import './EliasDataGrid.scss';
import { generateCellClass, renderCell } from './data-grid-helpers';
import { DEFAULT_PAGE_SIZE } from '../../../api/api';
import { useFilterContext, useFilterDispatchContext } from '../../contexts/FilterContext';
import { ExternalLeagueRoutesType } from '../../utils/router/external_urls';
import { ColumnMappingType } from '../../types/results';
import LastUpdated from './LastUpdated';

interface EliasDataGridProps {
  columns: GridColDef[];
  rows: (GridRowModel & { subRows?: GridRowModel[] })[]; // Allow rows to have optional subRows
  rowCount: number;
  handleDone: (sortByParam: string, pageParam: number) => void;
  linkUrls: ExternalLeagueRoutesType;
}

const DEFAULT_ROW_HEIGHT = 44;

function EliasDataGrid({
  columns,
  rows,
  rowCount,
  handleDone,
  linkUrls,
}: EliasDataGridProps) {
  const filterContext: any = useFilterContext();
  const filterDispatchContext: any = useFilterDispatchContext();
  // Using split here to handle potential secondary sorts (re: AE30-934)
  const sortBy: string = (filterContext.sortBy && filterContext.sortBy.split(',')[0]) || '-game__date';

  const initialSortByDirection = sortBy[0] === "-" ? "desc" : "asc";
  const initialSortByField = sortBy[0] === "-" ? sortBy.substring(1) : sortBy;

  const [sortByDirection, setSortByDirection] = React.useState<GridSortDirection>(initialSortByDirection);
  const [sortByField, setSortByField] = React.useState<string>(initialSortByField);

  const [sortModel, setSortModel] = React.useState<GridSortModel>([
    { sort: sortByDirection, field: sortByField }
  ]);

  const [rowCountState, setRowCountState] = React.useState(rowCount);
  
  // Track expanded rows with IDs as strings
  const [expandedRows, setExpandedRows] = React.useState<Set<string>>(new Set());

  // Process rows to include subRows when expanded
  const allRows = React.useMemo(() => {
    const result: GridRowModel[] = [];
    
    // First add all parent rows
    rows.forEach(row => {
      const processedRow = { ...row };
      result.push(processedRow);
      
      // If the row is expanded, add its children
      if (row.subRows?.length && expandedRows.has(String(row.id))) {
        row.subRows.forEach((subRow, idx) => {
          result.push({
            ...subRow,
            id: subRow.id || `${row.id}-sub-${idx}`,
            parentId: row.id,
            isChildRow: true // Add an isChildRow property to identify child rows for styling
          });
        });
      }
    });
    
    return result;
  }, [rows, expandedRows]);

  React.useEffect(() => {
    setRowCountState((prevRowCountState) =>
      rowCount !== undefined ? rowCount : prevRowCountState
    );
  }, [rowCount, setRowCountState]);

  // Toggle row expansion when chevron is clicked
  const handleToggleExpand = React.useCallback((rowId: string | number, event: React.MouseEvent) => {
    event.stopPropagation();
    
    const rowIdStr = String(rowId);
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(rowIdStr)) {
        next.delete(rowIdStr);
      } else {
        next.add(rowIdStr);
      }
      return next;
    });
  }, []);

  const getCustomCellClassName = (params: any) => (params.row.isChildRow ? 'child-row-cell' : '');

  const formattedGridColumns = columns.map((col: any, colIndex) => ({
    ...col,
    cellClassName: (params: any) => {
      // Combine the sort class with our custom class
      const baseClass = generateCellClass(sortByField)(params);
      const customClass = getCustomCellClassName(params);
      return `${baseClass} ${customClass}`.trim();
    },
    headerClassName: generateCellClass(sortByField),
    width: col.width,
    renderCell: (params: any) => {
      // Only add expansion control to the first column
      if (colIndex === 0 && params.row.subRows && params.row.subRows.length > 0) {
        const isExpanded = expandedRows.has(String(params.row.id));
        
        return (
          <Box 
            display="flex" 
            alignItems="center" 
            width="100%"
            onClick={(e) => {
              // Prevent the cell from being focused when the box is clicked
              e.currentTarget.blur();
            }}
          >
            <Box 
              component="div" 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleToggleExpand(params.row.id, e);
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                cursor: 'pointer',
                marginRight: 4,
                display: 'flex',
                alignItems: 'center',
                outline: 'none', // Remove outline
              }}
              sx={{
                '&:focus': {
                  outline: 'none',
                },
              }}
            >
              <ChevronRightIcon 
                color="action"
                sx={{
                  transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  outline: 'none', // Remove outline on the icon as well
                }}
              />
            </Box>
            {renderCell(col.field, linkUrls)(params)}
          </Box>
        );
      }
      
      return renderCell(col.field, linkUrls)(params);
    },
    renderHeader: (params: ColumnMappingType & GridColumnHeaderParams) => {
      if (params.colDef.description && params.colDef.description.length > 0) {
        return (
          <Tooltip title={params.colDef.description} placement="top">
            <strong>{params.colDef.headerName}</strong>
          </Tooltip>
        )
      }
      return (
        <strong>{params.colDef.headerName}</strong>
      )
    },
  }));

  const handleSortModelChange = (newSortModel: GridSortModel) => {
    const firstSortModel = newSortModel[0];
    if (firstSortModel) {
      setSortModel(newSortModel);
      const direction = firstSortModel.sort === 'desc' ? '-' : '';
      const ordering = `${direction}${firstSortModel.field}`;
      filterDispatchContext({
        type: 'sortByChanged',
        sortBy: ordering,
      });
      setSortByDirection(firstSortModel.sort);
      setSortByField(firstSortModel.field)
      handleDone(ordering, 0);
    }
  };

  const handlePageChange = (newPaginationModel: GridPaginationModel) => {
    filterDispatchContext({
      type: 'pageByChanged',
      page: newPaginationModel.page,
    });
    handleDone(filterContext.sortBy, newPaginationModel.page);
  };

  return (
    <Box style={{ margin: 5 }}>
      <DataGrid
        columns={formattedGridColumns}
        rows={allRows}
        rowCount={rowCountState}
        rowHeight={DEFAULT_ROW_HEIGHT}
        getRowId={(row) => String(row.id || row.rank)}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        columnHeaderHeight={DEFAULT_ROW_HEIGHT}
        pagination
        paginationMode="server"
        pageSizeOptions={[25]}
        onPaginationModelChange={handlePageChange}
        paginationModel={{ page: (filterContext.page ?? 0), pageSize: DEFAULT_PAGE_SIZE }}
        slots={{
          footer: LastUpdated,
        }}
        sortingOrder={['desc', 'asc']}
        sortModel={sortModel}
        sortingMode="server"
        onSortModelChange={(model) => handleSortModelChange(model)}
        sx={(theme) => ({
          [`.${gridClasses.main}`]: {
            overflow: 'unset',
            color: theme.palette.custom?.mediumGrey,
            fontSize: '12px',
          },
          [`.${gridClasses.columnHeaders}`]: {
            position: 'sticky',
            top: 70,
            backgroundColor: theme.palette.background.paper,
            zIndex: 1,
            borderBottom: 'none !important',
          },
          [`.${gridClasses.columnHeaderTitle}`]: {
            textTransform: 'capitalize',
          },
          [`.${gridClasses.columnSeparator}`]: {
            visibility: 'hidden',
            display: 'none',
          },
          [`.${gridClasses.columnHeadersInner}`]: {
            backgroundColor: theme.palette.custom?.lighterGrey,
            borderBottom: '2px solid rgba(224, 224, 224, 1)',
          },
          // Remove cell hover styling and focus only on row hover
          '& .MuiDataGrid-row:hover': {
            backgroundColor: theme.palette.custom?.blueGrey,
            // Make sure all cells in the row get the hover color
            '& .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
          },
          '& .Mui-selected': {
            backgroundColor: 'white !important',
          },
          // Apply background to all cells in child rows
          '& .MuiDataGrid-row:has(.child-row-cell)': {
            backgroundColor: theme.palette.custom?.lighterGrey,
            // Apply consistent background to all cells in the row
            '& .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
          },
          // Handle hover for child rows
          '& .MuiDataGrid-row:has(.child-row-cell):hover': {
            backgroundColor: theme.palette.custom?.blueGrey,
            // Make sure all cells in the hovered child row get the hover color
            '& .MuiDataGrid-cell': {
              backgroundColor: 'inherit',
            },
          },
          // Prevent cell focus outlines
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none !important',
          },
          '& .MuiDataGrid-cell:focus-visible': {
            outline: 'none !important',
          },
          marginY: '8px',
        })}
        autoHeight
      />
    </Box>
  );
}

export default EliasDataGrid;
